/**
 * /v1/game_frontend/* —— 對接商盤面回放 / 單局詳情。
 *
 * 後端已把回傳對齊 records:`data` 就是 BackendRecord 形狀,
 * 所以這裡只負責「打對端點」,回來直接灌 historyStore.selectedRecord 即可
 * 重用 HistoryDetail + 各遊戲盤面(不需 adapter)。
 */
import { useHttp } from '../http'
import type { BackendRecord } from '../records/types'
import type { FetchGameResultParams } from './types'
import { API_BASE } from '@/runtimeConfig'

// 端點 base —— auth + backend 同址，走 runtimeConfig 的 API_BASE。
// 🛑 不要在 module scope 快照成 const：API_BASE 是 live binding（見 runtimeConfig.ts），
//    要等 initRuntimeConfig() 之後才有值。於 fetchGameResult 內讀。

const maps = {
  /** GET /v1/game_frontend/games/{GAME_CODE}/results/{recordId} */
  result: (gameCode: string, recordId: string): string =>
    `/v1/game_frontend/games/${encodeURIComponent(gameCode)}/results/${encodeURIComponent(recordId)}`,
} as const

/**
 * 取得單局盤面詳情(回放)。
 *
 * 規格:
 *   path  : game_code(大寫)/ record_id
 *   query : operator_id / token(token 走 query,**不**放 header → 不觸發 CORS preflight)
 * 驗證走 query 一次性 token,不走登入 token → `skipAuth: true`。
 *
 * 回傳 = BackendRecord,可直接設給 historyStore.selectedRecord。
 *
 * @throws ApiError 401(token 失效)/ 業務錯
 */
export const fetchGameResult = (params: FetchGameResultParams): Promise<BackendRecord> => {
  const { gameCode, recordId, operatorId, token } = params

  return useHttp.get<BackendRecord>(
    // 後端 game_code 用大寫(e.g. YYZ3258)
    maps.result(gameCode.toUpperCase(), recordId),
    { operator_id: operatorId, token },
    {
      baseURL: API_BASE,
      skipAuth: true,
    },
  )
}
